// Drops Bugsnag events that originate entirely outside our app's bundle —
// most commonly the "RangeError: Failed to execute 'postMessage' on 'Window':
// Maximum call stack size exceeded" thrown by browser extensions (Grammarly,
// React/Redux DevTools, password managers, page-translation extensions, …)
// that inject scripts into every page and hand a cyclic / pathologically deep
// object to `window.postMessage` while talking to their own content script.
//
// We searched the entire app tree (and every installed dependency we ship to
// production) and there is no `Window.postMessage` call site we own and no
// `<iframe>` mounted on `/reports/new`. So when an event matches that exact
// signature **and** has no stack frame inside our app, the call site is in
// extension-injected code we cannot patch — there is nothing to fix on our
// side and the event is pure inbox noise.
//
// The filter is intentionally narrow:
//   1. Only RangeErrors whose message contains the postMessage / Window text.
//   2. Only when *no* stack frame is `inProject` — i.e. nothing in our bundle
//      participated. If a future regression ever introduces a real
//      `Window.postMessage` from our code, the event will still come through.

const POSTMESSAGE_SIGNATURE = "Failed to execute 'postMessage' on 'Window'";

const EXTENSION_FILE_PREFIXES = [
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
  'safari-web-extension://',
  'edge-extension://',
];

function isExtensionFrame(frame) {
  if (!frame || typeof frame.file !== 'string') return false;
  return EXTENSION_FILE_PREFIXES.some((prefix) => frame.file.startsWith(prefix));
}

function hasInProjectFrame(frames) {
  if (!Array.isArray(frames)) return false;
  return frames.some((frame) => frame && frame.inProject === true);
}

function allFramesAreExtensionOrAnonymous(frames) {
  return frames.every(
    (frame) =>
      isExtensionFrame(frame) ||
      !frame ||
      !frame.file ||
      frame.file === '<anonymous>' ||
      frame.file === 'native'
  );
}

function hasExtensionFrame(frames) {
  if (!Array.isArray(frames)) return false;
  return frames.some(isExtensionFrame);
}

export function isExtensionPostMessageNoise(event) {
  if (!event || !Array.isArray(event.errors) || event.errors.length === 0) {
    return false;
  }
  const err = event.errors[0];
  if (!err || err.errorClass !== 'RangeError') return false;
  if (
    typeof err.errorMessage !== 'string' ||
    err.errorMessage.indexOf(POSTMESSAGE_SIGNATURE) === -1
  ) {
    return false;
  }
  // Require an actual stack trace with at least one extension-origin frame —
  // otherwise we have no positive signal that this came from extension code
  // and we'd rather let the event through than mask a future regression.
  if (!Array.isArray(err.stacktrace) || err.stacktrace.length === 0) {
    return false;
  }
  if (!hasExtensionFrame(err.stacktrace)) return false;
  if (hasInProjectFrame(err.stacktrace)) return false;
  return allFramesAreExtensionOrAnonymous(err.stacktrace);
}

export function dropExtensionPostMessageNoise(event) {
  if (isExtensionPostMessageNoise(event)) return false;
  return undefined;
}

export default dropExtensionPostMessageNoise;
